/**
 * The one error shape the whole engine speaks (plan §5.1).
 *
 * Every failure — a stray bracket, a reference to a variable nobody supplies, a cycle, a unit that does
 * not add up, a division by zero at run time — is reported as a plain data object rather than thrown as
 * a subclassed `Error`, because the two consumers need it as *data*: the service returns the list from
 * `policies.validateDraft`, and the browser prints one line per equation while the admin types.
 */
export const EXPRESSION_ERROR_CODES = [
    /** The text is not a valid expression: an unbalanced bracket, a stray character, a missing operand. */
    'SYNTAX',
    /** `sqrt(x)` — only `min max round ceil floor abs if` exist. */
    'UNKNOWN_FUNCTION',
    /** The expression names something that is neither another equation nor a catalogue input. */
    'UNKNOWN_REFERENCE',
    /** The dependency graph loops: `a = b + 1`, `b = a - 1`, or an equation that names itself. */
    'CIRCULAR',
    /** Dimensional analysis refused it: `inspected + odometer`, or a declared unit the body cannot produce. */
    'UNIT_MISMATCH',
    /** An `ALWAYS` equation reached for a `TRADE_IN` equation or input (plan §5.1). */
    'SCOPE_LEAK',
    /** A SYSTEM id that does not exist, a duplicate id, or a CUSTOM id outside `x_[a-z][a-zA-Z0-9]*`. */
    'RESERVED_ID',
    /** One of the 35 SYSTEM outputs is not in the draft, so the platform would lose a figure it reads. */
    'MISSING_SYSTEM_OUTPUT',
    /** Run time: the divisor evaluated to zero. Blocks the result rather than clipping it (PP §7.5). */
    'DIVISION_BY_ZERO',
    /** Run time: the result is NaN or ±Infinity. */
    'NON_FINITE',
    /** Run time: a referenced input was not supplied, or is not a finite number. */
    'MISSING_INPUT',
    /** `round(1, 2)` — the function exists but not with that many arguments. */
    'ARITY',
];
export const expressionError = (code, detail, extra = {}) => ({ code, detail, ...extra });
/**
 * The only thrown carrier in the package: the tokenizer, the parser and the evaluator throw it so they
 * can fail deep inside a recursive descent, and every public entry point catches it and turns it back
 * into an `ExpressionError` in a list.
 */
export class EngineError extends Error {
    error;
    constructor(error) {
        super(`${error.code}: ${error.detail}`);
        this.name = 'EngineError';
        this.error = error;
    }
}
export const throwEngineError = (code, detail, extra = {}) => {
    throw new EngineError(expressionError(code, detail, extra));
};
/** Narrows an unknown catch binding; anything that is not ours is a genuine defect and is rethrown. */
export const asEngineError = (error) => {
    if (error instanceof EngineError) {
        return error.error;
    }
    throw error;
};
//# sourceMappingURL=errors.js.map